/**
 * D3: Assessment Storage (blueprints, attempts, reports)
 * D4: Assessment Session Recovery
 * Source: src/assessment/utils/assessmentStorage.ts
 */

import { USE_BACKEND, STORAGE_KEY, readJson, writeJson, removeKey } from '../helpers';
import { safeStorageWrite } from '../../utils/storageQuota';
const BLUEPRINTS_KEY = `${STORAGE_KEY}_assessment_blueprints`;
const ATTEMPTS_KEY = `${STORAGE_KEY}_assessment_attempts`;
const REPORTS_KEY = `${STORAGE_KEY}_assessment_reports`;
const SESSION_RECOVERY_KEY = `${STORAGE_KEY}_assessment_session`;

// ── Blueprints ──

export async function getAssessmentBlueprint<T = Record<string, unknown>>(id: string): Promise<T | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const all = readJson<Record<string, T>>(BLUEPRINTS_KEY, {});
  return all[id] ?? null;
}
export async function getAllAssessmentBlueprints<T = Record<string, unknown>>(): Promise<Record<string, T>> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson<Record<string, T>>(BLUEPRINTS_KEY, {});
}
export async function saveAssessmentBlueprint<T = Record<string, unknown>>(id: string, blueprint: T): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const all = readJson<Record<string, T>>(BLUEPRINTS_KEY, {});
  all[id] = blueprint;
  writeJson(BLUEPRINTS_KEY, all);
}

// ── Attempts ──

export async function getAssessmentAttempt<T = Record<string, unknown>>(id: string): Promise<T | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const all = readJson<Record<string, T>>(ATTEMPTS_KEY, {});
  return all[id] ?? null;
}
export async function getAllAssessmentAttempts<T = Record<string, unknown>>(): Promise<Record<string, T>> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson<Record<string, T>>(ATTEMPTS_KEY, {});
}
export async function saveAssessmentAttempt<T = Record<string, unknown>>(id: string, attempt: T): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const all = readJson<Record<string, T>>(ATTEMPTS_KEY, {});
  all[id] = attempt;
  writeJson(ATTEMPTS_KEY, all);
}

// ── Reports ──

export async function getAssessmentReport<T = Record<string, unknown>>(attemptId: string): Promise<T | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const all = readJson<Record<string, T>>(REPORTS_KEY, {});
  return all[attemptId] ?? null;
}
export async function getAllAssessmentReports<T = Record<string, unknown>>(): Promise<Record<string, T>> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson<Record<string, T>>(REPORTS_KEY, {});
}
export async function saveAssessmentReport<T = Record<string, unknown>>(attemptId: string, report: T): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const all = readJson<Record<string, T>>(REPORTS_KEY, {});
  all[attemptId] = report;
  writeJson(REPORTS_KEY, all);
}

// ── Assessment CRUD helpers ──

export async function deleteAssessmentAttempt(id: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  const attempts = readJson<Record<string, unknown>>(ATTEMPTS_KEY, {});
  delete attempts[id];
  writeJson(ATTEMPTS_KEY, attempts);
  const reports = readJson<Record<string, unknown>>(REPORTS_KEY, {});
  delete reports[id];
  writeJson(REPORTS_KEY, reports);
}
export async function clearAllAssessmentData(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  removeKey(BLUEPRINTS_KEY);
  removeKey(ATTEMPTS_KEY);
  removeKey(REPORTS_KEY);
}

// ── D4: Session Recovery ──

export async function getAssessmentSession<T = Record<string, unknown>>(): Promise<T | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson<T | null>(SESSION_RECOVERY_KEY, null);
}
export async function saveAssessmentSession<T = Record<string, unknown>>(session: T): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return safeStorageWrite(SESSION_RECOVERY_KEY, JSON.stringify(session));
}
export async function clearAssessmentSession(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  removeKey(SESSION_RECOVERY_KEY);
}